import {
  useLogger,
  useSwanStyleKeys,
  useSwanFontKeys,
  IdentityProvider,
  useUserContextTracker
} from '@vp/ubik-context'
import { CampaignProvider } from '@vp/campaign-callouts'
import { SwanFontNameEnum, SwanI18nProvider } from '@vp/swan'
import { useSetPriceConfigs, PageId } from '@vp/price-platform'
import { PageId as TilePageId } from '@vp/coupon-callout'
import { ProductTileProvider } from '@vp/ep-tiles'
import { FavoriteHeartProvider } from '@vp/favorites-heart'

import PageSections from './components/PageSections'
import { useProductListTracking } from './components/useProductListTracking'
import PageHeaderContainer, { getPageHeaderConfig } from './components/header/PageHeaderContainer'
import ExperimentsProvider from './components/ExperimentsProvider'
import { AppProvider } from './state/AppContext'

import { swanStyleKeys } from './utils/swanStyleKeys'
import { createAuthConfig } from './utils/auth/auth'

import { formatLocale } from './utils/buildHelpers'
import { FragmentProps } from './types/fragment-types'
import DebugInfo from './components/debugComponents/DebugInfo'

import {
  CATEGORY_PAGE_CA_BUSINESSCARDS_STOCKOPTIONS,
  CATEGORY_PAGE_EU_BUSINESSCARDS_STOCKOPTIONS,
  CATEGORY_PAGE_EU_CUSTOMSTICKERSINGLES_STOCKOPTIONS,
  CPV3_AUTOMATED_RECOMMENDATION_EU,
  CPV3_AUTOMATED_RECOMMENDATION_NA
} from './constants/supportedFeatureFlags'
import { RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION } from './constants/supportedExperiments'
import { UBIK_TRACKING_TYPES } from './types/UbikTrackingTypes'
import { isMcidSupportedForRecommendation } from './ab-tests/recommendationStack/recommendationStack'

export const Fragment = (props: FragmentProps) => {
  const {
    experimentKey,
    pageData,
    templateConfiguration,
    locale,
    pageDataFromBFF,
    dictionaryEntries,
    merchandisingCampaign,
    merchandisingCategoryId,
    defaultVatIncl,
    tenant,
    reviewsData,
    treatment,
    variationKey,
    spaceId,
    contentfulId,
    experiments,
    isPartOfDesignTemplateRollout,
    designTemplateData,
    automatedExperience,
    featureFlags,
    isPricingContextInitialized,
    productRecommendations,
    rawProductRecommendations,
    pageLevel,
    calloutsLocalization,
    productTileTranslations
  } = props

  const logger = useLogger()
  logger.setContext({
    name: 'category-page-fragment',
    'vista.team_tag': 'VPD-0003'
  })
  useSwanStyleKeys(swanStyleKeys)
  useSwanFontKeys([SwanFontNameEnum.Graphik, SwanFontNameEnum.Tiempos])

  const { backgroundColor, enableCampaignCallouts, sections, sidebar } = templateConfiguration

  const pageHeaderConfig = getPageHeaderConfig(templateConfiguration)
  const appProviderValue = {
    locale,
    environment: process.env.UBIK_ENVIRONMENT === 'prod' ? 'prod' : 'dev', // todo : think about UBIK_ENVIRONMENT
    enableCampaignCallouts,
    defaultVatIncl,
    dictionaryEntries,
    merchandisingCategoryId,
    backgroundColor: backgroundColor?.name,
    sidebarEnabled: sidebar?.enabled,
    tenant,
    mpvIds: pageDataFromBFF?.mpvs?.map((mpv) => mpv?.mpvId) || [],
    uploadFlowMpv: pageDataFromBFF?.uploadFlowMpv,
    reviewsData,
    spaceId,
    contentfulId,
    logger,
    experiments,
    isPartOfDesignTemplateRollout,
    designTemplateData,
    isPricingContextInitialized,
    calloutsLocalization
  }

  useProductListTracking({
    sections,
    mpvs: pageDataFromBFF?.mpvs,
    automatedExperience,
    featureFlags,
    productRecommendations
  })

  const tracker = useUserContextTracker()

  if (experimentKey && variationKey) {
    tracker({
      type: UBIK_TRACKING_TYPES.EXPERIMENT,
      name: experimentKey,
      variation: variationKey
    })
  }
  if (treatment) {
    tracker({
      type: UBIK_TRACKING_TYPES.TREATMENT,
      name: treatment
    })
  }
  if (merchandisingCampaign) {
    tracker({
      type: UBIK_TRACKING_TYPES.CAMPAIGN,
      name: merchandisingCampaign
    })
  }

  const categoryPageBusinessCardsStockOptions =
    featureFlags?.[CATEGORY_PAGE_EU_BUSINESSCARDS_STOCKOPTIONS]
  if (categoryPageBusinessCardsStockOptions !== undefined) {
    tracker({
      type: UBIK_TRACKING_TYPES.FEATURE_FLAG,
      name: CATEGORY_PAGE_EU_BUSINESSCARDS_STOCKOPTIONS,
      enabled: categoryPageBusinessCardsStockOptions
    })
  }

  const categoryPageCABusinessCardsStockOptions =
    featureFlags?.[CATEGORY_PAGE_CA_BUSINESSCARDS_STOCKOPTIONS]
  if (categoryPageCABusinessCardsStockOptions !== undefined) {
    tracker({
      type: UBIK_TRACKING_TYPES.FEATURE_FLAG,
      name: CATEGORY_PAGE_CA_BUSINESSCARDS_STOCKOPTIONS,
      enabled: categoryPageCABusinessCardsStockOptions
    })
  }

  const categoryPageCustomStickersStockOptions =
    featureFlags?.[CATEGORY_PAGE_EU_CUSTOMSTICKERSINGLES_STOCKOPTIONS]
  if (categoryPageCustomStickersStockOptions !== undefined) {
    tracker({
      type: UBIK_TRACKING_TYPES.FEATURE_FLAG,
      name: CATEGORY_PAGE_EU_CUSTOMSTICKERSINGLES_STOCKOPTIONS,
      enabled: categoryPageCustomStickersStockOptions
    })
  }

  const automatedRecommendationsEu = featureFlags?.[CPV3_AUTOMATED_RECOMMENDATION_EU]
  if (automatedRecommendationsEu !== undefined) {
    tracker({
      type: UBIK_TRACKING_TYPES.FEATURE_FLAG,
      name: CPV3_AUTOMATED_RECOMMENDATION_EU,
      enabled: automatedRecommendationsEu
    })
  }

  const automatedRecommendationsNa = featureFlags?.[CPV3_AUTOMATED_RECOMMENDATION_NA]
  if (automatedRecommendationsNa !== undefined) {
    tracker({
      type: UBIK_TRACKING_TYPES.FEATURE_FLAG,
      name: CPV3_AUTOMATED_RECOMMENDATION_NA,
      enabled: automatedRecommendationsNa
    })
  }

  if (
    isMcidSupportedForRecommendation(merchandisingCategoryId, locale) &&
    experiments[RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION]
  ) {
    tracker({
      type: UBIK_TRACKING_TYPES.EXPERIMENT,
      name: RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION,
      variation: experiments[RELATED_PRODUCTS_FREQUENTLY_ORDERED_AUTOMATION]
    })
  }
  useSetPriceConfigs({
    locale,
    tenant,
    pageId: PageId.Category,
    tracking: {
      pageSection: 'Category Page',
      pageStage: 'Discover',
      pageName: `${merchandisingCategoryId}:Category Page`
    }
  })

  return (
    <IdentityProvider auth={createAuthConfig(locale)}>
      <CampaignProvider tenant={tenant} locale={locale}>
        <AppProvider {...appProviderValue}>
          <DebugInfo />
          <ExperimentsProvider>
            <SwanI18nProvider locale={locale} translations={productTileTranslations}>
              {/* ProductTileProvider subsumes CouponCalloutProvider — it initialises
                  the coupon-callout context internally, so the separate
                  CouponCalloutProvider wrapper is no longer needed. */}
              <ProductTileProvider
                tenant={tenant}
                locale={formatLocale(locale, true)}
                pageId={TilePageId.Category}
                pageSection="Category Page"
              >
                <FavoriteHeartProvider pageSection="Category Page">
                  <PageHeaderContainer
                    configuration={pageHeaderConfig}
                    sidebar={templateConfiguration.sidebar}
                    pageDataFromBFF={pageDataFromBFF}
                  />
                  <PageSections
                    sections={sections}
                    pageCategoryPageV3={pageData}
                    pageDataFromBFF={pageDataFromBFF}
                    templateConfiguration={templateConfiguration}
                    featureFlags={featureFlags}
                    {...(automatedExperience !== undefined && { automatedExperience })}
                    {...(productRecommendations !== undefined && { productRecommendations })}
                    {...(rawProductRecommendations !== undefined && { rawProductRecommendations })}
                    {...(pageLevel != null && { pageLevel })}
                  />
                </FavoriteHeartProvider>
              </ProductTileProvider>
            </SwanI18nProvider>
          </ExperimentsProvider>
        </AppProvider>
      </CampaignProvider>
    </IdentityProvider>
  )
}
